/**
 * Minimap (Block 5) — a small windowed overview around the player. It shows only
 * EXPLORED terrain (read from the fog's chunked memory), the player's position,
 * and markers for the tent/home the player has seen. Live things (mobs, loot,
 * other players) are deliberately absent — the minimap is memory, not a radar.
 *
 * Windowed rather than whole-world: the draft map is hundreds of tiles across, so
 * we render a fixed box of tiles around the player, redrawn on a throttle.
 */
import type { FarmCtx } from './context';
import { oreByVariant } from '../../map/ores';
import { TILE } from '../../map/world';
import { el } from '../../dom';
import { gicon } from '../../ui/icons';

const SIZE = 148;        // canvas px (square)
const WIN = 96;          // tiles shown across the window
const UPDATE_MS = 400;   // redraw throttle

const TERRAIN_COLOR: Record<string, string> = {
  grass: '#4c7a3e',
  water: '#2c5a82',
  till: '#6d4a30',
};

export interface Minimap {
  /**
   * The map panel's home — the free strip on the right of the world. The panel
   * only rests here while CLOSED; opening moves it into a centred screen overlay
   * (see below), so the map no longer unfolds out of this corner square.
   */
  el: HTMLElement;
  /**
   * The open/close button, mounted SEPARATELY from the panel.
   *
   * The two used to be one column, which is why the toggle ended up wherever the
   * panel had room: 187px (landscape) / 610px (portrait) from the action button,
   * i.e. nowhere near the thumb that has to press it. They are decoupled so the
   * BUTTON can join the right-hand thumb rail while the PANEL keeps the empty
   * strip it needs — re-anchoring the panel to the rail instead would have put it
   * straight through the Build/Village dropdown.
   */
  toggleEl: HTMLElement;
  update(nowMs: number): void;
  destroy(): void;
}

export function createMinimap(c: FarmCtx): Minimap {
  const canvas = document.createElement('canvas');
  canvas.width = SIZE; canvas.height = SIZE;
  // The backing store stays SIZE² (the draw code is written in those units); CSS
  // scales it down when the clamp above shrinks the box, rather than cropping it.
  canvas.className = 'block';
  canvas.style.cssText = 'display:block;width:100%;height:100%;image-rendering:pixelated';
  const map = document.createElement('div');
  // Clamped to a share of the viewport as well as its natural size: a fixed 148px
  // square is a third of a landscape phone's height, and the box then ran off the
  // TOP of the play area (its top computed to −6px at 844×390).
  map.className = 'rounded-md border-2 border-border/80 bg-black/50 shadow-lift overflow-hidden';
  map.style.width = `min(${SIZE}px, 34vw)`; map.style.height = `min(${SIZE}px, 34vh)`;
  map.appendChild(canvas);

  // Collapsed by default: a 148px square parked over the world is a lot of screen
  // to spend on a map you glance at, and on a landscape phone it swallowed the
  // bottom-right corner next to the action button. It now lives behind a small
  // corner button that toggles it (state kept for the session only — the player
  // re-opens it when they want to orient themselves).
  const toggle = el('button',
    'tb-minimap-toggle w-10 h-10 grid place-items-center rounded-md border-2 border-border/80 bg-black/55 hover:border-gold shadow-lift',
    { title: 'Map' }, gicon('map', 16));
  // The PANEL only. Right edge, clear of the bottom band (stick · hotbar · action)
  // below it and of the Build/Village cluster above it. `tb-minimap-box` in app.css
  // owns the offset so the band's height is declared once, not copied here.
  const box = el('div', 'tb-minimap-box absolute right-3 z-20 flex flex-col items-end gap-1.5', {}, map);
  // The BUTTON lives in the right-hand thumb rail (`.tb-thumb-rail`, farm.ts) on a
  // phone; on desktop the rail is a zero-size wrapper pinned above the minimap box,
  // so the pair still reads as one control there.
  const toggleWrap = el('div', 'tb-minimap-toggle-wrap flex', {}, toggle);

  // ─── Opened map = a centred modal, not a corner panel ───────────────────────
  // Opening it used to leave the panel parked in the `box` above, so on a phone the
  // map unfolded out of the little corner square and hugged the screen edge — you
  // had to look for where it had grown to. It now moves into a full-screen backdrop
  // and sits in the middle, at a predictable size in either orientation.
  const closeBtn = el('button',
    'w-11 h-11 grid place-items-center rounded-md border-2 border-border/80 bg-black/60 text-white text-xl leading-none',
    { title: 'Close the map' }, '✕');
  closeBtn.setAttribute('aria-label', 'Close the map');
  const frame = el('div', 'flex flex-col items-center gap-2', {});
  const overlay = el('div',
    'tb-map-overlay fixed inset-0 z-50 flex flex-col items-center justify-center gap-2 bg-black/70 p-3',
    { style: 'touch-action:none' },
    frame, closeBtn);
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', 'Map');

  let open = false;
  const applyOpen = () => {
    if (open) {
      // The canvas keeps its SIZE² backing store; only the CSS box grows. vmin so
      // the square fits portrait and landscape alike, and 320px-wide screens too.
      map.style.width = map.style.height = 'min(78vmin, 420px)';
      frame.appendChild(map);
      map.style.display = '';
      document.body.appendChild(overlay);
    } else {
      overlay.remove();
      map.style.width = `min(${SIZE}px, 34vw)`; map.style.height = `min(${SIZE}px, 34vh)`;
      box.appendChild(map);
      map.style.display = 'none';
    }
    toggle.title = open ? 'Hide the map' : 'Show the map';
  };

  /**
   * An open map is modal: input must not fall through to the world.
   *
   * Before this, the hero kept walking under the map — a held WASD key, a
   * tap-to-walk through the backdrop, or a joystick drag that was still live when
   * the map opened. pointerup/pointercancel and keyup are deliberately NOT
   * swallowed: they are how the joystick, the camera drag and the key set RELEASE.
   * Eating them is what would strand the hero walking after the map closed.
   */
  const swallow = (e: Event) => {
    if (!open) return;
    // A key event dispatched straight at `window` has a target that is NOT a Node,
    // and `contains()` throws on one — so the type is checked, not assumed.
    const t = e.target;
    // The map's own controls still work — and so does the toggle that opened it,
    // which would otherwise be the one button the block made unusable.
    if (t instanceof Node && (overlay.contains(t) || toggleWrap.contains(t))) return;
    e.stopPropagation();
    if (e.cancelable) e.preventDefault();
  };
  const onKey = (e: KeyboardEvent) => {
    if (!open) return;
    if (e.key === 'Escape') { e.stopPropagation(); e.preventDefault(); setOpen(false); return; }
    swallow(e);
  };
  const BLOCKED = ['pointerdown', 'pointermove', 'click', 'wheel', 'touchstart', 'touchmove'] as const;
  const bind = (add: boolean) => {
    const fn = add ? window.addEventListener : window.removeEventListener;
    for (const type of BLOCKED) fn.call(window, type, swallow, { capture: true, passive: false } as AddEventListenerOptions);
    fn.call(window, 'keydown', onKey as EventListener, { capture: true } as AddEventListenerOptions);
  };

  function setOpen(next: boolean) {
    if (open === next) return;
    open = next;
    if (open) {
      // Stop the hero the moment the map covers the world: whatever was held when
      // it opened would otherwise still be held when it closed.
      c.keys.clear();
      c.player?.setKeys(0, 0);
      bind(true);
    } else {
      bind(false);
    }
    applyOpen();
    if (open) { last = -1e9; update(performance.now()); }
  }

  toggle.addEventListener('click', () => setOpen(!open));
  closeBtn.addEventListener('click', () => setOpen(false));
  // Tapping the darkened world around the map closes it — the usual modal escape
  // on a phone, where there is no Escape key.
  overlay.addEventListener('click', (e) => { if (e.target === overlay) setOpen(false); });
  applyOpen();

  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingEnabled = false;
  let last = -1e9;

  function update(nowMs: number) {
    if (!open) return;                       // collapsed → nothing to redraw
    if (nowMs - last < UPDATE_MS) return;
    last = nowMs;
    const { world, fog, player } = c;
    if (!world || !player) return;
    // With fog of war switched off (the default) there is no explored-memory to
    // read: the whole map is known and lit, so the minimap draws every tile at full
    // brightness rather than going blank.
    const explored = (tx: number, ty: number) => (fog ? fog.explored(tx, ty) : true);
    const inSight = (tx: number, ty: number) => (fog ? fog.inSightNow(tx, ty) : true);
    const px = player.x / TILE, py = player.y / TILE;
    const cx = Math.round(px), cy = Math.round(py);
    const half = WIN / 2;
    const scale = SIZE / WIN;

    ctx.fillStyle = '#05070c';
    ctx.fillRect(0, 0, SIZE, SIZE);

    // Explored terrain only. Unexplored stays the black backdrop.
    for (let ty = cy - half; ty < cy + half; ty++) {
      for (let tx = cx - half; tx < cx + half; tx++) {
        if (tx < 0 || ty < 0 || tx >= world.cols || ty >= world.rows) continue;
        if (!explored(tx, ty)) continue;
        const terr = world.terrain[ty * world.cols + tx];
        ctx.fillStyle = TERRAIN_COLOR[terr] ?? '#3a3a3a';
        const sx = (tx - (cx - half)) * scale, sy = (ty - (cy - half)) * scale;
        ctx.fillRect(sx, sy, Math.ceil(scale), Math.ceil(scale));
        // Dim the currently-out-of-sight (remembered) tiles slightly.
        if (!inSight(tx, ty)) { ctx.fillStyle = 'rgba(8,20,32,0.45)'; ctx.fillRect(sx, sy, Math.ceil(scale), Math.ceil(scale)); }
      }
    }

    const toScreen = (tx: number, ty: number): [number, number] =>
      [(tx - (cx - half)) * scale, (ty - (cy - half)) * scale];

    // Features on the explored ground: trees (dark green), rocks/ore (grey), water
    // life aside. Only what's in the window + already seen.
    const dot = (tx: number, ty: number, color: string) => {
      if (tx < cx - half || tx >= cx + half || ty < cy - half || ty >= cy + half) return;
      if (!explored(tx, ty)) return;
      const [sx, sy] = toScreen(tx, ty);
      ctx.fillStyle = color; ctx.fillRect(sx, sy, Math.max(1, Math.ceil(scale)), Math.max(1, Math.ceil(scale)));
    };
    for (const d of world.decor) {
      if (d.kind === 'tree') dot(d.tx, d.ty, '#2c5423');
      // A vein is drawn in ITS metal now (map/ores.ts) — the minimap is how you
      // decide which way to walk, and "there is ore over there" is a much weaker
      // reason to walk than "there is mythril over there".
      else if (d.kind === 'ore_rock') dot(d.tx, d.ty, oreByVariant(d.variant).color);
      else if (d.kind === 'rock' || d.kind === 'boulder' || d.kind === 'hillock' || d.kind === 'mountain') dot(d.tx, d.ty, '#75757f');
      else if (d.kind === 'bush' || d.kind === 'mushrooms') dot(d.tx, d.ty, '#3e7d4f');
    }
    // The player's farmed plots: planted = leaf-green, tilled/empty = soil brown.
    for (const p of c.farm?.plots ?? []) dot(p.tx, p.ty, p.cropId ? '#9ccf5a' : '#7a5232');

    // Markers for places the player has seen: the tent/home, and a built campfire.
    // `shape` is not decoration: at this size the markers are a few pixels across,
    // and telling gold from violet from orange by hue alone fails for a colour-blind
    // player. The silhouette carries the meaning, the colour reinforces it.
    const marker = (tx: number, ty: number, color: string, shape: 'square' | 'tent' | 'horse' = 'square') => {
      if (!explored(tx, ty)) return;
      const [sx, sy] = toScreen(tx, ty);
      if (sx < 0 || sy < 0 || sx > SIZE || sy > SIZE) return;
      ctx.fillStyle = color;
      ctx.strokeStyle = 'rgba(0,0,0,0.75)';
      ctx.lineWidth = 1;
      if (shape === 'tent') {
        // A triangle — the roofline reads as a tent even at 7px.
        ctx.beginPath();
        ctx.moveTo(sx, sy - 4); ctx.lineTo(sx + 4, sy + 3); ctx.lineTo(sx - 4, sy + 3);
        ctx.closePath(); ctx.fill(); ctx.stroke();
      } else if (shape === 'horse') {
        // A diamond — distinct from both the square markers and the round player dot.
        ctx.beginPath();
        ctx.moveTo(sx, sy - 4); ctx.lineTo(sx + 4, sy); ctx.lineTo(sx, sy + 4); ctx.lineTo(sx - 4, sy);
        ctx.closePath(); ctx.fill(); ctx.stroke();
      } else {
        ctx.fillRect(sx - 2, sy - 2, 5, 5);
        ctx.strokeRect(sx - 2.5, sy - 2.5, 6, 6);
      }
    };
    // THE TENT, where it actually stands.
    //
    // This used to plot `world.farm.tx/ty` — the farm ORIGIN — and call it the
    // tent. That was true only while the tent could not move. It can now be packed
    // up and pitched anywhere, so the gold marker was pointing at a patch of ground
    // the tent had left, which is worse than no marker: the one thing you open the
    // map to find was reliably in the wrong place.
    //
    // `c.tent` is the server's answer (see building-menu tentApi), and `pitched`
    // is the only honest test — tx/ty survive stowing, because respawn still needs
    // them.
    const tent = c.tent;
    if (tent?.pitched && tent.tx !== null && tent.ty !== null) {
      marker(tent.tx, tent.ty, '#e8c15a', 'tent');                    // tent = gold
    }
    // THE HORSE. It wanders, it is easy to lose, and walking to it is deliberately
    // part of owning one (there is no recall) — which only works as a design if you
    // can find it. Drawn from the live riding layer, and skipped while mounted,
    // since then it is under you and its marker would just sit on the player dot.
    const horse = c.riding?.mounted ? null : c.riding?.position();
    if (horse) marker(horse.tx, horse.ty, '#c9a0ff', 'horse');        // horse = violet
    if (c.campfire) marker(c.campfire.tx, c.campfire.ty, '#e8703a');  // campfire = orange

    // Player: a bright dot at the centre with a facing-agnostic ring.
    const [sx, sy] = toScreen(px, py);
    ctx.beginPath();
    ctx.arc(sx, sy, 3, 0, Math.PI * 2);
    ctx.fillStyle = '#ffffff'; ctx.fill();
    ctx.lineWidth = 1.5; ctx.strokeStyle = '#3a86ff'; ctx.stroke();
  }

  return {
    el: box,
    toggleEl: toggleWrap,
    update,
    // Leaving the view with the map open must not leave the window listeners (and
    // therefore a dead input block) behind.
    destroy() { setOpen(false); box.remove(); toggleWrap.remove(); overlay.remove(); },
  };
}
